/**
 * Parsing Rand amounts typed by humans or exported by spreadsheets.
 *
 * South Africa uses a SPACE for thousands and a COMMA for decimals:
 * R 250 000,50 is two hundred and fifty thousand rand and fifty cents.
 * Stripping commas as if they were thousands separators — the obvious
 * thing to do, and what we were doing — turns that into R25 000 050, a
 * hundredfold overstatement, silently, on import.
 *
 * The same file may also carry US/UK formatting if it came out of a
 * tool set to en-US, so we cannot simply swap the rule: we have to
 * work out which separator is the decimal one, per value.
 *
 * Returns integer CENTS so nothing downstream has to touch a float.
 */
/** Space variants spreadsheets emit: normal, non-breaking, narrow, thin. */
const SPACES = /[\s\u00a0\u202f\u2009]/g;
/** A space between two digits: the South African thousands separator. */
const SPACE_GROUPED = /\d[\s\u00a0\u202f\u2009]+\d/;
/** Currency noise: R, ZAR, and the rand sign if anyone uses it. */
const CURRENCY = /(?:ZAR|R)/gi;
/**
 * Work out which separator is the decimal one and split on it. `s` has
 * had its spaces removed already; `spaceGrouped` says whether it had
 * spaces between digits before that.
 *
 * Someone who groups thousands with a space does not also group with a
 * comma, so in a space-grouped value a lone comma or dot can only be the
 * decimal separator. Without that rule "R 250 000,505" read the comma as
 * grouping and came out as R 250 000 505, a thousand times the amount.
 */
function splitDecimal(s, spaceGrouped) {
    // At least one digit: a lone "." or "," is not zero.
    if (!s || !/^[\d.,]+$/.test(s) || !/\d/.test(s))
        return null;
    const lastComma = s.lastIndexOf(',');
    const lastDot = s.lastIndexOf('.');
    const commaCount = (s.match(/,/g) ?? []).length;
    const dotCount = (s.match(/\./g) ?? []).length;
    let decimalSeparator = null;
    if (lastComma !== -1 && lastDot !== -1) {
        // Both present: whichever comes last is the decimal separator, and
        // the other one is grouping. "250,000.50" vs "250.000,50".
        decimalSeparator = lastComma > lastDot ? ',' : '.';
    }
    else if (lastComma !== -1) {
        // Only commas. One comma with 1–2 trailing digits is SA decimal
        // notation; a single comma with exactly 3 is grouping ("1,234"),
        // and several commas are always grouping. Three exceptions: a leading
        // zero ("0,999" cannot be grouping, because nobody writes 999 as
        // "0999"), a value already grouped with spaces, and more than three
        // digits before the comma. "250000,505" cannot be thousands grouping,
        // because grouping puts a separator every three digits; it is
        // "R 250 000,505" typed without the spaces, the same slip.
        const trailing = s.length - lastComma - 1;
        const groupable = trailing === 3 && !s.startsWith('0') && !spaceGrouped && lastComma <= 3;
        decimalSeparator = commaCount === 1 && !groupable ? ',' : null;
    }
    else if (lastDot !== -1) {
        // Only dots. A single dot is a decimal point in both SA and US
        // usage; repeated dots can only be grouping ("1.234.567").
        decimalSeparator = dotCount === 1 ? '.' : null;
    }
    let whole;
    let fraction = '';
    if (decimalSeparator) {
        const cut = s.lastIndexOf(decimalSeparator);
        whole = s.slice(0, cut);
        fraction = s.slice(cut + 1);
    }
    else {
        whole = s;
    }
    // Anything left in the integer part is grouping punctuation.
    whole = whole.replace(/[.,]/g, '');
    if (whole === '')
        whole = '0';
    if (!/^\d*$/.test(whole) || !/^\d*$/.test(fraction))
        return null;
    return { whole, fraction, decimalSeparator };
}
/**
 * Parse a human/spreadsheet Rand string into cents.
 *
 * Handles: "R 250 000,50" · "250,000.50" · "R1 234 567,89" · "1234.56"
 * · "1,234" (thousands) · "250,5" (decimal) · "(1 234,00)" (negative)
 * · "" and junk (null).
 *
 * Refuses a third decimal digit where a person typed it the South African
 * way (a decimal comma, or thousands grouped with spaces). The rand has
 * cents, so "R 250 000,505" is a slip of the finger, and guessing which
 * slip it was is how a payment ends up a thousand times too large. A dot
 * decimal with no spaces is how spreadsheets and accounting exports write
 * numbers, so those still round to the cent as before.
 */
export function parseZarAmountDetailed(raw) {
    if (typeof raw !== 'string')
        return null;
    let s = raw.trim();
    if (!s)
        return null;
    // Accounting negatives: (1 234,00)
    let negative = false;
    if (/^\(.*\)$/.test(s)) {
        negative = true;
        s = s.slice(1, -1);
    }
    s = s.replace(CURRENCY, '');
    const spaceGrouped = SPACE_GROUPED.test(s);
    s = s.replace(SPACES, '');
    if (s.startsWith('-')) {
        negative = true;
        s = s.slice(1);
    }
    if (s.startsWith('+'))
        s = s.slice(1);
    const split = splitDecimal(s, spaceGrouped);
    if (!split)
        return null;
    const { whole, fraction, decimalSeparator } = split;
    if (fraction.length > 2 && (decimalSeparator === ',' || spaceGrouped))
        return null;
    // Round to cents on the digits themselves — Math.round(n * 100) on a
    // float misrounds values like 1.005.
    const centsFraction = `${fraction}00`.slice(0, 2);
    let cents = Number(whole) * 100 + Number(centsFraction);
    if (fraction.length > 2 && Number(fraction[2]) >= 5)
        cents += 1;
    if (!Number.isFinite(cents))
        return null;
    return { cents: negative ? -cents : cents, decimalSeparator, negative };
}
/** Cents, or null if the text is not an amount. */
export function parseZarAmount(raw) {
    return parseZarAmountDetailed(raw)?.cents ?? null;
}
/** Cents for a strictly positive amount, or null. Used by the importer. */
export function parsePositiveZarAmount(raw) {
    const cents = parseZarAmount(raw);
    return cents !== null && cents > 0 ? cents : null;
}
/**
 * A plain number typed the South African way, for counts, rates and
 * readings that are not money: "12,5", "1 250", "0,75", "-3". Same
 * separator rules as the Rand parser, with no limit on decimals, because
 * a median or a percentage is not rounded to the cent.
 *
 * Exists because `<input type="number">` drops a decimal comma: an
 * indicator reading typed as "12,5" was stored as 125.
 */
export function parseSaDecimal(raw) {
    if (typeof raw !== 'string')
        return null;
    let s = raw.trim();
    if (!s)
        return null;
    let negative = false;
    if (s.startsWith('-') || s.startsWith('−')) {
        negative = true;
        s = s.slice(1).trim();
    }
    else if (s.startsWith('+')) {
        s = s.slice(1).trim();
    }
    const spaceGrouped = SPACE_GROUPED.test(s);
    s = s.replace(SPACES, '');
    const split = splitDecimal(s, spaceGrouped);
    if (!split)
        return null;
    const n = Number(`${split.whole}.${split.fraction || '0'}`);
    if (!Number.isFinite(n))
        return null;
    return negative && n !== 0 ? -n : n;
}
/**
 * An amount in some other currency, in that currency's hundredths, the
 * way a South African writes it: "USD 50 000,00". For showing what a
 * document said before anyone has put a rand figure on it. Never call
 * this with a ZAR amount you mean to store; that is formatZarCents.
 */
export function formatCurrencyMinor(minor, currency) {
    const negative = minor < 0;
    const abs = Math.round(Math.abs(minor));
    const units = Math.floor(abs / 100)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f');
    const frac = (abs % 100).toString().padStart(2, '0');
    return `${negative ? '−' : ''}${currency.toUpperCase()}\u202f${units},${frac}`;
}
